import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router";
import SearchInput from "../../../components/shared/SearchInput";
import { globalFilter } from "../../../utils/arrays";
import { useCompanyStore, usePermissionStore } from "../../../store";
import {
  queryClient,
  useGetUserCompanyDetails,
  useGetUserRoleAndPermission,
  useSwitchCompany,
} from "../../../queries";
import {
  useGetControlPlaneCompanies,
  useSwitchCompanyOnControlPlane,
} from "../../../queries/login";
import {
  Permission,
  Action,
  Resource,
} from "../../../utils/interfaces/Companies";
import { useGrantDetailsStore } from "../../../store/useGrantDetailsStore";
import { useOrgTemplateStore } from "../../../store/useOrgTemplateStore";
import { determineUserAccessToResource } from "../../../utils/auth";

export type CompanyType = {
  name: string;
  dba: string;
  id: string;
};

export const SwitchCompanyDropdown: React.FC<any> = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const pathname = useLocation().pathname;

  const { companyData, setCompanyData } = useCompanyStore();
  const { setPermission, permission } = usePermissionStore();

  // Control plane is the single source of truth — returns companies across ALL cells
  const { data: cpCompanies } = useGetControlPlaneCompanies();

  const filteredCompanies = React.useMemo(() => {
    if (!cpCompanies?.length) return [];
    return globalFilter(cpCompanies, searchTerm, ["name"]);
  }, [searchTerm, cpCompanies]);

  const { mutate: switchUserCompany } = useSwitchCompany();
  const { mutate: switchCPCompany } = useSwitchCompanyOnControlPlane();
  const { refetch: getPermission } = useGetUserRoleAndPermission();
  const { data: currentCompanyDetails, refetch: refetchUserCompanyDetails } =
    useGetUserCompanyDetails();

  const { clear: grantDataClear } = useGrantDetailsStore();
  const { clear: orgDataClear } = useOrgTemplateStore();

  const canCreateCompany = determineUserAccessToResource(
    permission?.aclList || [],
    Resource.Company,
    Action.Create
  );

  async function switchCompany(companyId: string) {
    if (companyData?.id === companyId) return;
    if (pathname.includes("options")) {
      navigate("/options/esopOverview");
    }
    setLoading(true);
    sessionStorage.setItem("companyId", companyId.toString());

    // 1. Sync active_company cookie on Control Plane (best-effort)
    switchCPCompany(companyId, {
      onError: () => {
        // Non-blocking — data-plane switch is the authoritative action
      },
    });

    // 2. Switch on the Data Plane
    switchUserCompany(
      { companyId },
      {
        onSuccess: () => {
          getPermission().then((permission) => {
            setPermission(permission.data as Permission);
          });
          refetchUserCompanyDetails();
          setLoading(false);
          setIsOpen(false);
        },
        onError: () => {
          toast("Error in switching company", {
            type: "error",
            autoClose: 2000,
          });
          setLoading(false);
        },
      }
    );
    queryClient.invalidateQueries();
    queryClient.refetchQueries();
    grantDataClear();
    orgDataClear();
  }

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsOpen]);

  React.useEffect(() => {
    if (currentCompanyDetails) {
      setCompanyData(currentCompanyDetails);
    }
  }, [currentCompanyDetails]);

  // Derive cell info for the active company directly from the control plane list
  const activeCpCompany = companyData?.id
    ? cpCompanies?.find((c) => c.id === companyData.id)
    : undefined;

  return (
    <>
      <div>
        {loading ? (
          <div className="items-center px-6 py-2">
            <p className="text-sm animate-pulse font-medium">Switching...</p>
          </div>
        ) : (
          <div className="relative" ref={dropdownRef}>
            {/* ── Trigger button ── */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`bg-gray-100 px-4 py-7 h-10 md:w-64 text-sm border-b flex items-center justify-between ${
                companyData?.name ? "border-primary" : ""
              }`}
              type="button"
            >
              <span className="text-gray-700 sm:block hidden whitespace-nowrap">
                <div className="w-full max-w-[200px] overflow-hidden">
                  <p
                    className="text-start font-semibold truncate overflow-hidden whitespace-nowrap"
                    title={companyData?.name}
                  >
                    {companyData?.name}
                  </p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <p className="text-xs1 font-semi-medium text-gray-light whitespace-nowrap text-ellipsis overflow-clip text-start">
                      {companyData?.companyMetaData?.typeOfTheCompany ===
                      "PUBLIC"
                        ? "Public Listed"
                        : companyData?.companyMetaData?.typeOfTheCompany ===
                          "PUBLIC_UNLISTED"
                        ? "Public Unlisted"
                        : "Private"}
                    </p>
                    {activeCpCompany?.cell?.region && (
                      <span className="text-xs1 bg-blue-50 text-blue-500 px-1 rounded leading-tight">
                        {activeCpCompany.cell.region}
                      </span>
                    )}
                  </div>
                </div>
              </span>

              <svg
                className={`w-5 h-5 transition-transform flex-shrink-0 ${
                  isOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* ── Dropdown panel ── */}
            {isOpen && (
              <div className="absolute top-full -right-16 md:right-0 w-72 bg-white rounded-sm shadow-lg z-10 px-3 py-2">
                <div className="mb-2">
                  <SearchInput
                    placeholder="Search Company"
                    value={searchTerm}
                    onChange={(e: any) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="max-h-72 overflow-y-auto scroll-smooth">
                  {filteredCompanies.map((company, index) => {
                    const isActive = companyData?.id === company.id;
                    return (
                      <div
                        key={company.id}
                        className={`p-2 px-1 ${
                          index !== filteredCompanies.length - 1
                            ? "border-b border-gray-200 border-dashed"
                            : ""
                        } ${
                          isActive
                            ? "cursor-not-allowed"
                            : "hover:bg-gray-100 cursor-pointer"
                        }`}
                        onClick={() => !isActive && switchCompany(company.id)}
                      >
                        <div className="flex items-center justify-between">
                          <p
                            className={`text-sm font-medium truncate ${
                              isActive ? "text-gray-400" : "text-gray-700"
                            }`}
                          >
                            {company.name}
                          </p>
                          {isActive && (
                            <span className="ml-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-green-400" />
                          )}
                        </div>

                        {/* Cell / region badge — available directly from control plane */}
                        {company.cell && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <span
                              className={`text-xxs ${
                                isActive ? "text-gray-300" : "text-gray-400"
                              }`}
                            >
                              {company.cell.name}
                            </span>
                            {company.cell.region && (
                              <span
                                className={`text-xxs px-1 rounded leading-tight ${
                                  isActive
                                    ? "bg-gray-100 text-gray-300"
                                    : "bg-blue-50 text-blue-500"
                                }`}
                              >
                                {company.cell.region}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {filteredCompanies.length === 0 && (
                    <p className="flex items-center px-4 py-2 text-gray-700 text-sm">
                      No companies available
                    </p>
                  )}
                </div>

                {/* ── Add Company CTA ── */}
                {canCreateCompany && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <button
                      type="button"
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-primary hover:bg-gray-50 rounded"
                      onClick={() => {
                        setIsOpen(false);
                        navigate("/company/createCompany");
                      }}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add New Company
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
